import { useEffect, useMemo, useState } from "react";
import { http, downloadUrl, getUserLabels, toastApiError } from "../lib/api";
import { toast } from "../lib/notify";
import { safePath } from "../lib/nav";
import { useNavigate } from "react-router-dom";
import { fmtTL, fmtNum } from "../lib/format";
import { FileDown, ChevronDown } from "lucide-react";
import { FilterTh, useColFilters, isFilterActive } from "./ColFilters";
import { periodLabel } from "./claims/claimGrouping";
import ClaimHistoryRow from "./claims/ClaimHistoryRow";
import UnpricedBanner from "./claims/UnpricedBanner";
import CorrectionHistoryDialog from "./claims/CorrectionHistoryDialog";

const selCls = "px-2.5 py-1.5 text-xs border border-slate-300 rounded-md bg-white";
const COLS = [
  { key: "period", label: "Dönem", type: "multi", fmt: periodLabel },
  { key: "category_name", label: "Ana Hizmet" },
  { key: "work_days", label: "Toplam Çalışma Gün", type: "number" },
  { key: "meal_days", label: "Toplam Yemek Gün", type: "number" },
  { key: "service_amount", label: "Brüt Ücretler Tutarı", type: "number" },
  { key: "meal_amount", label: "Yemek Tutarı", type: "number" },
  { key: "overhead_amount", label: "Firma Gider Payı", type: "number" },
  { key: "grand_total", label: "Toplam Tutar", type: "number" },
];
// Taşeron hakedişinde yemek/yol ayrımı ve firma gider payı yok → bu sütunlar gösterilmez
const colsFor = (direction) => (direction === "expense" ? COLS.filter((c) => !["meal_amount", "overhead_amount"].includes(c.key)) : COLS);
const statusMatch = (r, statusF) => !statusF || (statusF === "draft" ? r.is_draft : !r.is_draft);
const sum = (rows, k) => rows.reduce((a, r) => a + (Number(r[k]) || 0), 0);

export default function ClaimHistory({ direction, projectId, subcontractorId, returnTo = "/musteri-hakedis", testid = "claim-history", focusInvoiceId = null }) {
  const [rows, setRows] = useState(null);
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);   // kapalı gelir; başlığa tıklayınca açılır (Hakediş Oluştur sonrası yeni fatura açık gelir)
  const [openInv, setOpenInv] = useState(null);
  const [statusF, setStatusF] = useState("");
  const [userLabels, setUserLabels] = useState({});
  const [regions, setRegions] = useState([]);
  useEffect(() => { getUserLabels().then(setUserLabels); }, []);
  useEffect(() => { if (direction !== "expense") http.get("/regions", { params: projectId ? { project_id: projectId } : {} }).then((r) => setRegions(r.data)).catch(() => setRegions([])); }, [projectId, direction]);
  const params = useMemo(() => ({ direction, ...(projectId ? { project_id: projectId } : {}), ...(subcontractorId ? { subcontractor_id: subcontractorId } : {}) }), [direction, projectId, subcontractorId]);

  const [reloadKey, setReloadKey] = useState(0);
  const [regenerating, setRegenerating] = useState(null);
  const [savingCorrection, setSavingCorrection] = useState(null);
  useEffect(() => {
    setRows(null); if (!reloadKey) setOpenInv(null);
    http.get("/claims/history", { params }).then((r) => setRows(r.data)).catch(() => setRows([]));
  }, [params, reloadKey]);
  // Hakediş Oluştur sonrası: bölüm açılır, yeni fatura detayı açık gelir (düzeltme satırı eklenebilsin)
  useEffect(() => {
    if (!focusInvoiceId) return;
    setOpen(true); setOpenInv(focusInvoiceId); setReloadKey((k) => k + 1);
    setTimeout(() => document.querySelector(`[data-testid='${testid}-body']`)?.scrollIntoView({ behavior: "smooth", block: "start" }), 400);
  }, [focusInvoiceId, testid]);
  // ± düzeltme satırı: bölgenin DÜZELTME FARKLARI şubesine veri girişi + fatura kalemleri yeniden üretilir
  const saveCorrection = async (inv, payload) => {
    setSavingCorrection(inv.id);
    try {
      const { data } = await http.post(`/claims/invoices/${inv.id}/corrections`, payload);
      toast.success(`${data.location_name} şubesine ${payload.total > 0 ? "+" : ""}${fmtTL(payload.total)} düzeltme işlendi — ${data.invoice_no} toplamı ${fmtTL(data.total_amount)}`);
      setReloadKey((k) => k + 1);
      return true;
    } catch (e) { toastApiError(e); return false; } finally { setSavingCorrection(null); }
  };
  // fatura düzeltme satırı → DÜZELTME FARKLARI kaydı: satırda entry_id varsa doğrudan, yoksa bölge + ad + tutar eşlemesiyle
  const corrEntryId = async (inv, line) => {
    if (line.entry_id) return line.entry_id;
    const { data: list } = await http.get(`/claims/invoices/${inv.id}/corrections`);
    const norm = (s) => (s || "").toLocaleUpperCase("tr").trim();
    const hit = list.find((e) => norm(e.description) === norm(line.name) && (!line.region || norm(e.region_name) === norm(line.region)) && Math.abs((e.customer_total || 0) - line.total) < 0.01)
      || list.find((e) => norm(e.description) === norm(line.name));
    return hit?.id;
  };
  // düzeltme satırını kaldır: kayıt silinir, kalemler yeniden üretilir
  const deleteCorrection = async (inv, line) => {
    if (!window.confirm(`"${line.name}" düzeltme satırı (${fmtTL(line.total)}) kaldırılsın mı?\n\nDÜZELTME FARKLARI şubesindeki kayıt silinir ve fatura kalemleri yeniden üretilir.`)) return;
    setSavingCorrection(inv.id);
    try {
      const eid = await corrEntryId(inv, line);
      if (!eid) { toast.error("Düzeltme kaydı bulunamadı — Veri Girişi › düzeltme şubesinden kontrol edin"); return; }
      const { data } = await http.delete(`/claims/invoices/${inv.id}/corrections/${eid}`);
      toast.success(`Düzeltme satırı kaldırıldı — ${data.invoice_no} toplamı ${fmtTL(data.total_amount)}`);
      setReloadKey((k) => k + 1);
    } catch (e) { toastApiError(e); } finally { setSavingCorrection(null); }
  };
  // düzeltme satırını yerinde düzenle: kayıt güncellenir (ad, miktar, birim, tutar, bölge), kalemler yeniden üretilir
  const editCorrection = async (inv, line, payload) => {
    setSavingCorrection(inv.id);
    try {
      const eid = await corrEntryId(inv, line);
      if (!eid) { toast.error("Düzeltme kaydı bulunamadı — Veri Girişi › düzeltme şubesinden kontrol edin"); return false; }
      const { data } = await http.put(`/claims/invoices/${inv.id}/corrections/${eid}`, payload);
      toast.success(`Düzeltme satırı güncellendi (${payload.total > 0 ? "+" : ""}${fmtTL(payload.total)}) — ${data.invoice_no} toplamı ${fmtTL(data.total_amount)}`);
      setReloadKey((k) => k + 1);
      return true;
    } catch (e) { toastApiError(e); return false; } finally { setSavingCorrection(null); }
  };
  // Hakedişi Onayla: taslak → onaylı fatura (PROJE GELİRLERİ › <Proje> HAKEDİŞ ÖDEMELERİ), KDV/tevkifat hesaplanır, dönem kilitlenir
  const [approving, setApproving] = useState(null);
  const approveClaim = async (inv, payload, gross) => {
    if (!window.confirm(`${inv.invoice_no} hakedişi onaylansın mı?\n\nFatura: ${fmtTL(gross)} (KDV %${payload.kdv_rate}${payload.tevkifat_rate ? `, tevkifat ${payload.tevkifat_rate / 10}/10` : ""})\nPROJE GELİRLERİ › ${inv.project_name} HAKEDİŞ ÖDEMELERİ kalemine bağlanır ve ${periodLabel(inv.period)} dönemi kilitlenir.`)) return;
    setApproving(inv.id);
    try {
      const { data } = await http.post(`/claims/invoices/${inv.id}/approve`, payload);
      toast.success(`${data.invoice_no} onaylandı — ${data.budget_item_name} · fatura ${fmtTL(data.total_amount)} · ${periodLabel(data.period)} dönemi kilitlendi${data.notified?.length ? ` · muhasebeye e-posta gönderildi (${data.notified.length})` : " · bildirim gidecek muhasebe kullanıcısı yok"}`, { duration: 9000 });
      setReloadKey((k) => k + 1);
      const don = safePath(new URLSearchParams(window.location.search).get("don"));   // maliyet analizinden gelindiyse onay sonrası aynı filtrelerle geri
      if (don) navigate(don);
    } catch (e) { toastApiError(e); } finally { setApproving(null); }
  };
  const [histRegion, setHistRegion] = useState(null);   // { projectId, regionId } → Düzeltme Geçmişi penceresi
  // Onayı Kaldır: onaylı → taslak (bütçe bağı kalkar, KDV'siz kalem toplamına döner), dönem kilidi açılır; düzeltme eklemeye devam edilebilir
  const unapproveClaim = async (inv) => {
    if (!window.confirm(`${inv.invoice_no} hakedişinin onayı kaldırılsın mı?\n\nFatura taslağa döner (bütçe kalemi bağı ve KDV tutarları kalkar), ${periodLabel(inv.period)} dönemi kilidi açılır; düzeltme satırı ekleyip yeniden onaylayabilirsiniz.`)) return;
    setApproving(inv.id);
    try {
      await http.patch(`/invoices/${inv.id}/unapprove`);
      toast.success(`${inv.invoice_no} taslağa döndü — ${periodLabel(inv.period)} dönemi kilidi açıldı; düzeltme ekleyip "Hakedişi Onayla" ile yeniden onaylayın`, { duration: 7000 });
      setOpenInv(inv.id); setReloadKey((k) => k + 1);
    } catch (e) { toastApiError(e); } finally { setApproving(null); }
  };
  // Taslak Yenile: fatura kalemleri güncel kurala göre yeniden üretilir (brüt ücretler + firma gider payı kalemleri + yan haklar, bölge bazında)
  const regenerate = async (inv) => {
    if (!window.confirm(`${inv.invoice_no} taslak faturasının kalemleri güncel hesaplama kuralına göre yeniden oluşturulsun mu?\n\nMevcut kalemler silinir; bölge bazında Brüt Ücretler, firma gider payı kalemleri ve yemek / kahvaltı / yol satırları yazılır. Fatura no ve tarihler korunur.`)) return;
    setRegenerating(inv.id);
    try {
      const { data } = await http.post(`/claims/invoices/${inv.id}/regenerate`);
      toast.success(`${data.invoice_no} yenilendi — ${data.items} kalem, ${fmtTL(data.previous_total)} → ${fmtTL(data.total_amount)}`);
      setReloadKey((k) => k + 1);
    } catch (e) { toastApiError(e); } finally { setRegenerating(null); }
  };

  const all = useMemo(() => (rows || []).filter((r) => statusMatch(r, statusF)).sort((a, b) => (b.is_draft ? 1 : 0) - (a.is_draft ? 1 : 0)), [rows, statusF]);
  const cols = useMemo(() => colsFor(direction), [direction]);
  const showMeal = direction !== "expense";
  const [hf, setHf, visible, autoOpts] = useColFilters(all, cols);
  const filtering = cols.some((c) => isFilterActive(c, hf[c.key]));
  const showProject = new Set((rows || []).map((r) => r.project_name)).size > 1;
  const draftCount = (rows || []).filter((r) => r.is_draft).length;
  const unpriced = (rows || []).some((r) => r.meal_unpriced_days > 0);

  return (
    <div className="bg-white border border-slate-200 rounded-lg" data-testid={testid} data-open={open ? "1" : "0"}>
      <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open} data-testid={`${testid}-toggle`}
        className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-slate-50 rounded-lg transition-colors">
        <div>
          <div className="text-sm font-semibold text-slate-900 flex items-center gap-2 flex-wrap">Önceki Hakedişler
            {rows !== null && <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 text-[10px] font-bold" data-testid={`${testid}-count`}>{rows.length}</span>}
            {draftCount > 0 && <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[10px] font-bold uppercase" data-testid={`${testid}-draft-count`}>{draftCount} taslak</span>}
          </div>
          <div className="text-xs text-slate-500">Dönem · ana hizmet · puantaj gün toplamları · tutarlar; satıra tıklayınca fatura kalemleri. {open ? "Kapatmak" : "Açmak"} için tıklayın.</div>
        </div>
        <ChevronDown className={`w-4 h-4 text-slate-500 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="px-4 pb-4 space-y-3 border-t border-slate-100 pt-3" data-testid={`${testid}-body`}>
          <div className="flex flex-wrap items-center justify-end gap-2">
            {unpriced && <span className="mr-auto text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1" data-testid={`${testid}-no-meal-rate`}>Bazı personelin yemek ücreti tanımsız: personel kartına YEMEK ücreti girin ya da Sabit Tanımlar › Ücretler'de "Yemek Ücreti" tanımlayın.</span>}
            <select value={statusF} onChange={(e) => setStatusF(e.target.value)} data-testid={`${testid}-filter-status`} className={selCls}>
              <option value="">Durum: Tümü</option>
              <option value="draft">Yalnız Taslak</option>
              <option value="approved">Yalnız Onaylı</option>
            </select>
            <a href={downloadUrl(`/claims/history/export?${new URLSearchParams(params).toString()}`)} target="_blank" rel="noreferrer" className="excel-btn" data-testid={`${testid}-export`}>
              <FileDown className="w-4 h-4" /> Excel{"\u2019"}e Aktar
            </a>
          </div>

          {rows === null && <div className="text-xs text-slate-400">Yükleniyor…</div>}
          {rows !== null && showMeal && <UnpricedBanner rows={rows} backTo={returnTo} testid={`${testid}-unpriced`} />}
          {rows !== null && (
            <div className="border border-slate-200 rounded-lg overflow-x-auto">
              <table className="w-full tbl">
                <thead className="bg-slate-50">
                  <tr>
                    {cols.map((c, i) => (
                      <th key={c.key} className={`px-3 py-2.5 text-xs uppercase tracking-wider text-slate-600 font-semibold align-bottom ${i >= 2 ? "text-right" : "text-left"} ${i >= 2 ? "max-w-[150px]" : ""}`}>
                        <FilterTh col={c} hf={hf} setHf={setHf} testid={`${testid}-hf-${c.key}`} align={i >= 2 ? "right" : "left"} autoOptions={autoOpts[c.key]} wrap />
                      </th>
                    ))}
                    <th className="px-3 py-2.5 text-xs uppercase tracking-wider text-slate-600 font-semibold text-right align-bottom">Durum</th>
                  </tr>
                </thead>
                <tbody>
                  {visible.map((inv) => (
                    <ClaimHistoryRow key={inv.id} inv={inv} open={openInv === inv.id} onToggle={() => setOpenInv(openInv === inv.id ? null : inv.id)} testid={testid} direction={direction} returnTo={returnTo} userLabels={userLabels} showProject={showProject} showMeal={showMeal} subcontractorId={subcontractorId} onRegenerate={direction !== "expense" ? regenerate : undefined} regenerating={regenerating} regions={regions} onSaveCorrection={direction !== "expense" ? saveCorrection : undefined} onEditCorrection={direction !== "expense" ? editCorrection : undefined} onDeleteCorrection={direction !== "expense" ? deleteCorrection : undefined} savingCorrection={savingCorrection} onApprove={direction !== "expense" ? approveClaim : undefined} onUnapprove={direction !== "expense" ? unapproveClaim : undefined} approving={approving} onHistory={direction !== "expense" ? (projectId, regionId) => setHistRegion({ projectId, regionId }) : undefined} />
                  ))}
                  {visible.length === 0 && (
                    <tr><td colSpan={showMeal ? 9 : 7} className="py-8 text-center text-sm text-slate-500" data-testid={`${testid}-empty`}>{rows.length === 0 ? "Daha önce oluşturulmuş onaylı veya taslak hakediş yok" : "Filtrelerle eşleşen hakediş yok"}</td></tr>
                  )}
                </tbody>
                {visible.length > 0 && (
                  <tfoot className="bg-slate-50 border-t border-slate-200">
                    <tr data-testid={`${testid}-footer`}>
                      <td colSpan={2} className="px-3 py-2.5 text-xs font-semibold text-slate-700 uppercase tracking-wider">{filtering ? "Filtre Toplamı" : "Toplam"} — {visible.length} hakediş</td>
                      <td className="numeric-cell px-3 py-2.5 text-xs font-bold">{fmtNum(sum(visible, "work_days"))}</td>
                      <td className="numeric-cell px-3 py-2.5 text-xs font-bold">{fmtNum(sum(visible, "meal_days"))}</td>
                      <td className="numeric-cell px-3 py-2.5 text-xs font-bold">{fmtTL(sum(visible, "service_amount"))}</td>
                      {showMeal && <td className="numeric-cell px-3 py-2.5 text-xs font-bold">{fmtTL(sum(visible, "meal_amount"))}</td>}
                      {showMeal && <td className="numeric-cell px-3 py-2.5 text-xs font-bold" data-testid={`${testid}-footer-overhead`}>{fmtTL(sum(visible, "overhead_amount"))}</td>}
                      <td className="numeric-cell px-3 py-2.5 text-sm font-bold text-slate-900">{fmtTL(sum(visible, "grand_total"))}</td>
                      <td></td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          )}
        </div>
      )}
      {histRegion && <CorrectionHistoryDialog regions={regions.filter((r) => r.project_id === histRegion.projectId)} defaultRegionId={histRegion.regionId} onClose={() => setHistRegion(null)} returnTo={returnTo} testid={`${testid}-corr-history`} />}
    </div>
  );
}
